import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import VideoChatController from './VideoChatController';
import { connect } from 'react-redux';
import { AppMapProps, IAppState } from '../../redux/reducer';
import { ActionType, useAppState } from '../../state/AppState';
import {
  IParticipantForUser,
  IPartyForUser,
  IPartyStageForUser,
  TAreaKey,
  TUserId,
} from '../../PartyClientResponse';
import { MAX_VIDEO_CONNECTION_RETRY_ATTEMPTS } from '../../components/party/AppConstants';
import OT from '@opentok/client';
import OTVideoController from './OTVideoController';
import { LOG_EVENT } from '../../services/LoggingService';

const HAS_AV_BEEN_SET = 'HAS_AV_BEEN_SET';
const MOST_RECENT_VIDEO_KEY = 'MOST_RECENT_VIDEO_KEY';
const MOST_RECENT_AUDIO_KEY = 'MOST_RECENT_AUDIO_KEY';
const PUBLISHER_RETRY_INTERVAL = 5000;

export type TAVPermission = 'unknown' | 'allowed' | 'denied';

const NO_OP = (...args: any) => {};
const DEBUG_LOG = NO_OP;

let hasBeenUnmuted = false;

// In some browsers it won't autoplay the video unless the window has been interacted with
// So, save for for after the first click
const playOnInteract = new Map<HTMLElement, () => Promise<void>>();
const onFirstClick = () => {
  playOnInteract.forEach((fn) => fn());
  document.onclick = null;
};
document.onclick = onFirstClick;

function serialize(obj: any) {
  return JSON.stringify(serializeRecurse(obj, new Set(), 0), null, 2);
}

function serializeRecurse(obj: any, set: Set<any>, depth: number) {
  if (typeof obj === 'object') {
    if (depth > 8) {
      return String(obj);
    }
    const tmp: any = {};

    for (let key in obj) {
      if (typeof obj[key] !== 'function') {
        if (!set.has(obj[key])) {
          set.add(obj[key]);
          tmp[key] = serializeRecurse(obj[key], set, depth + 1);
        }
      }
    }
    return tmp;
  }
  return obj;
}
export class AVApi {
  static getStreamVideoDeviceId = (track: MediaStreamTrack | null): string => {
    return track?.getSettings().deviceId || '';
  };

  static getStreamAudioDeviceId = (track: MediaStreamTrack | null): string => {
    return track?.getSettings().deviceId || '';
  };
}

const useForceUpdate = () => {
  const [, forceUpdate] = useState();
  const update = useCallback(() => {
    forceUpdate(undefined);
  }, [forceUpdate]);
  return update;
};

function _VideoControlProvider({ children }: { children?: React.ReactNode } & IAppState) {
  const [vc] = useState<VideoChatController | null>(null);
  const {
    enableAV,
    user,
    participants,
    isAudioInputBlocked: _isAudioInputBlocked,
    currentArea,
    party,
    dispatch,
  } = useAppState();

  const partyKey = party?.key;

  const [_localVideoEnabled, _setLocalVideoEnabled] = useState<boolean>(true);
  const [availableDevices, setAvailableDevices] = useState<OT.Device[]>([]);
  const [needsAVForArea, setNeedsAVForArea] = useState<boolean>(false);
  const [videoDeviceId, setVideoDeviceId] = useState<string | null>(
    null
    // localStorage.getItem(MOST_RECENT_VIDEO_KEY)
  );
  const [audioDeviceId, setAudioDeviceId] = useState<string | null>(
    localStorage.getItem(MOST_RECENT_AUDIO_KEY)
  );

  const participant = participants.find((p) => p.uid === user?.uid);
  const isAudioForceMuted = (participant?.tags || []).includes('audio-muted');
  const isAudioInputBlocked =
    _isAudioInputBlocked || !needsAVForArea || !enableAV || isAudioForceMuted;

  const isVideoForceMuted = (participant?.tags || []).includes('video-muted');
  const localVideoEnabled = _localVideoEnabled && needsAVForArea && enableAV && !isVideoForceMuted;

  const forceUpdate = useForceUpdate();

  const audioDevices = availableDevices.filter((d) => d.kind === 'audioInput');
  const videoDevices = availableDevices.filter((d) => d.kind === 'videoInput');

  const requestLocalStreamPermissions = useCallback(() => {
    if (enableAV && vc) {
      const cancel = vc.requestLocalStreamPermissions();
      return () => {
        cancel();
      };
    }
  }, [vc, enableAV]);

  const setLocalAudio = useCallback(
    (deviceId: string) => {
      setAudioDeviceId(deviceId);
      // if (vc) {
      //   vc.setLocalAudio(deviceId);
      // }
    },
    [setAudioDeviceId]
  );

  const setLocalVideo = useCallback(
    async (deviceId: string) => {
      if (await OTVideoController.isValidDevice(deviceId, 'videoInput')) {
        DEBUG_LOG('[ot] setting video device: ', deviceId);
        setVideoDeviceId(deviceId);
      } else {
        DEBUG_LOG('[ot] invalid video device: ', deviceId);
      }
      // if (vc) {
      //   vc.setLocalVideo(deviceId);
      // }
    },
    [setVideoDeviceId]
  );

  // Poll to refresh available devices
  useEffect(() => {
    let timeout: NodeJS.Timeout;

    const refresh = async () => {
      // Ignore if we haven't asked for permission yet
      if (enableAV) {
        const devices = await OTVideoController.getDevices();
        setAvailableDevices(devices);

        // const devices = await navigator.mediaDevices.enumerateDevices();
        // if (JSON.stringify(devices) !== JSON.stringify(availableDevices)) {
        //   setAvailableDevices(devices);
        // }
      }
      timeout = setTimeout(refresh, 4000);
    };

    timeout = setTimeout(refresh, 0);

    return () => {
      clearTimeout(timeout);
    };
  }, [setAvailableDevices, enableAV]);

  const setParticipantsInSameArea = useCallback(
    (currentParticipantArea: TAreaKey, stage: IPartyStageForUser) => {
      const participantInSameArea = Object.values(stage.participants).filter(
        (p) => p.currentArea === currentParticipantArea
      );
      // if (
      //   stage.spotlight?.participant &&
      //   !participantInSameArea.find((p) => p.uid === stage.spotlight?.participant.uid)
      // ) {
      //   participantInSameArea.push(stage.spotlight.participant);
      // }
      if (vc) {
        vc.setParticipantsInSameArea(participantInSameArea);
      }
    },
    [vc]
  );

  // Lets us know that we have already allowed it
  // so we don't get the popup window on returning
  const setHasAVBeenSet = useCallback(
    (hasBeenSet: boolean) => {
      if (hasBeenSet) {
        localStorage.setItem(HAS_AV_BEEN_SET, 'yes');
      } else {
        localStorage.removeItem(HAS_AV_BEEN_SET);
      }
      forceUpdate();
    },
    [forceUpdate]
  );

  // cleanup streams using the a/v hardware (turns off camera light and icon)
  const cleanupMediaDeviceAccess = useCallback(() => {
    if (vc) {
      vc.cleanupMediaDeviceAccess();
    }
  }, [vc]);

  const remoteStreams: Partial<{ [uid in TUserId]: MediaStream }> = vc?.getRemoteStreams() || {};

  // ---------------- OpenTok -------------------- //

  const [{ sessionId, sessionToken }, setSessionDetails] = useState<{
    sessionId: string | null;
    sessionToken: string | null;
  }>({ sessionId: null, sessionToken: null });
  const prevSessionDetails = useRef<{ sessionId: string | null; sessionToken: string | null }>({
    sessionId: null,
    sessionToken: null,
  });

  const [avPermission, setPermissionChanged] = useState<TAVPermission>('unknown');
  const [session, setSession] = useState<OT.Session | null>(null);
  const [publisher, setPublisher] = useState<OT.Publisher | null>(null);
  const [publisherMounted, setPublisherMounted] = useState<boolean>(false);
  const [sessionConnected, setSessionConnected] = useState<boolean>(false);
  const [streams, setStreams] = useState<Partial<{ [id in TUserId]: OT.Stream }>>({});
  const [localVideoTrack, setLocalVideoTrack] = useState<MediaStreamTrack | null>(null);
  const [localAudioTrack, setLocalAudioTrack] = useState<MediaStreamTrack | null>(null);

  useEffect(() => {
    let unmounted = false;

    if (!user?.uid) {
      return;
    }

    DEBUG_LOG('[ot] user connect sessionToken: ', sessionToken, ' and sessionId: ', sessionId);
    if (
      prevSessionDetails.current.sessionId === sessionId &&
      prevSessionDetails.current.sessionToken === sessionToken
    ) {
      return;
    }

    prevSessionDetails.current.sessionToken = sessionToken;
    prevSessionDetails.current.sessionId = sessionId;

    if (!sessionId || !sessionToken) {
      return;
    }

    const newSession = OT.initSession(process.env.REACT_APP_OPENTOK_API_KEY!, sessionId);

    newSession.on('streamCreated', (event) => {
      if (unmounted) return;

      DEBUG_LOG('[ot] streamCreated: ', event.target?.connection?.data);
      if (event.target.sessionId !== newSession?.sessionId) {
        DEBUG_LOG(
          `[ot] error, received stream for stale session: ${event.target.sessionId}, current sessionId: ${newSession?.sessionId}`
        );
      }

      const uid = event.stream.name;
      if (!uid) {
        console.error('stream has no UID');
        return;
      }

      setStreams((streams) => ({ ...streams, [uid as TUserId]: event.stream }));
    });

    newSession.on('streamPropertyChanged', (event) => {
      DEBUG_LOG('streamPropertyChanged', event.changedProperty, event.newValue);

      if (event.stream.name === user?.uid) {
        DEBUG_LOG('[ot] self stream stopped');
        // DEBUG_LOG(
        //   'accessAllowed',
        //   event.target.getPublisherForStream(event.stream)?.accessAllowed
        // );
        // setPermissionChanged('denied');
      }

      if (unmounted) return;

      const uid = event.stream.name;
      if (!uid) {
        console.error('stream has no UID');
        return;
      }
      setStreams((streams) => ({ ...streams, [uid as TUserId]: event.stream }));
    });

    newSession.on('connectionDestroyed', (event) => {
      DEBUG_LOG('connection destroyed ', event.connection.connectionId, event.reason);
    });

    newSession.on('sessionDisconnected', (event) => {
      DEBUG_LOG('disconnected from ', event.reason);
      setSessionConnected(false);
    });

    newSession.on('streamDestroyed', (event) => {
      if (unmounted) return;
      // event.preventDefault();

      const uid = event.stream.name;
      if (!uid) {
        console.error('stream has no UID');
        return;
      }

      DEBUG_LOG('streamDestroyed, streamId: ', event.stream.streamId, 'reason: ', event.reason);
      setStreams((streams) => {
        const { [uid as TUserId]: toRemove, ...newStreams } = streams;
        return newStreams;
      });
    });

    newSession.connect(sessionToken, (err) => {
      if (unmounted) return;

      if (err) {
        console.error(err);
      } else {
        DEBUG_LOG('[ot] session connect successful');
        setSessionConnected(true);
      }
    });

    setSession(newSession);

    return () => {
      DEBUG_LOG('[ot] unmounting session');
      unmounted = true;
      newSession.disconnect();
      setSessionConnected(false);
    };
  }, [setSession, setSessionConnected, sessionId, sessionToken, user?.uid]);

  const connectToSession = useCallback(
    (sessionId?: string, sessionToken?: string) => {
      setSessionDetails({
        sessionId: sessionId || null,
        sessionToken: sessionToken || null,
      });
    },
    [setSessionDetails]
  );

  const [selfVideoMounted, setSelfVideo] = useState<HTMLElement | null>(null);
  const shouldMountPublisher =
    currentArea && user?.uid && selfVideoMounted && enableAV && needsAVForArea;

  useEffect(() => {
    let cancel = false;
    let newPublisher: OT.Publisher | null;
    let retryTimeout: NodeJS.Timeout;

    const initPub = async () => {
      if (!shouldMountPublisher) {
        return;
      }

      const options: OT.PublisherProperties = {
        insertMode: 'append',
        // insertDefaultUI: false,
        mirror: false,
        resolution: '1280x720',
        frameRate: 30,
        autoGainControl: true,
        echoCancellation: true,
        // disableAudioProcessing: true,
        width: '100%',
        height: '100%',
        name: user!.uid,
        // @ts-ignore
        // enableStereo: true,
        audioBitrate: 196000,
        // showControls: false,
        style: {
          audioLevelDisplayMode: 'off',
          buttonDisplayMode: 'off',
          nameDisplayMode: 'off',
          archiveStatusDisplayMode: 'off',
          backgroundImageURI: user?.photoURL,
        },
      };

      const prevVideoDeviceId = localStorage.getItem(MOST_RECENT_VIDEO_KEY);
      if (
        prevVideoDeviceId &&
        (await OTVideoController.isValidDevice(prevVideoDeviceId, 'videoInput'))
      ) {
        options.videoSource = prevVideoDeviceId;
      }

      const prevAudioDeviceId = localStorage.getItem(MOST_RECENT_AUDIO_KEY);
      if (
        prevAudioDeviceId &&
        (await OTVideoController.isValidDevice(prevAudioDeviceId, 'audioInput'))
      ) {
        options.audioSource = prevAudioDeviceId;
      }

      newPublisher = OT.initPublisher('self-video', options, (err) => {
        if (err) {
          console.error('[ot] publisher initialization error: ', err);
          if (err.name === 'OT_USER_MEDIA_ACCESS_DENIED') {
            setPermissionChanged('denied');
            clearTimeout(retryTimeout);
            retryTimeout = setTimeout(initPub, PUBLISHER_RETRY_INTERVAL);
          }
          return;
        }
        if (!cancel && newPublisher) {
          setPermissionChanged('allowed');
          setPublisher(newPublisher);
          LOG_EVENT('webcam_enabled', { party_key: partyKey });
        }
      });

      newPublisher.on('streamDestroyed', (event) => {
        if (
          event.stream?.connection.connectionId === newPublisher?.stream?.connection.connectionId
        ) {
          // event.preventDefault();
          DEBUG_LOG('pub streamDestroyed', event);
        }
      });

      // Should happen when webcam permissions are revoked
      newPublisher.once('destroyed', (event) => {
        DEBUG_LOG('pub destroyed', event);
        setPublisher(null);
        setPublisherMounted(false);
        setPermissionChanged('denied');
        retryTimeout = setTimeout(initPub, PUBLISHER_RETRY_INTERVAL);
      });

      newPublisher.on('accessDialogClosed', (event) => {
        DEBUG_LOG('[ot] access dialog closed', event);
        // setPermissionChanged('allowed');
      });

      newPublisher.on('accessAllowed', () => {
        DEBUG_LOG('[ot] access allowed');
        setPermissionChanged('allowed');
        // newPublisher?.publishAudio(enableAV);
        // newPublisher?.publishVideo(enableAV);
      });

      newPublisher.on('mediaStopped', (event) => {
        DEBUG_LOG('[ot] media stopped', event);
        setPermissionChanged('denied');
        clearTimeout(retryTimeout);
        retryTimeout = setTimeout(initPub, PUBLISHER_RETRY_INTERVAL);
      });

      newPublisher.on('accessDenied', () => {
        console.error('[ot] access denied');
        // Removing for now as this is firing every time we poll
        // LOG_EVENT('webcam_access_denied', { party_key: partyKey });
        setPermissionChanged('denied');
        clearTimeout(retryTimeout);
        retryTimeout = setTimeout(initPub, PUBLISHER_RETRY_INTERVAL);
      });

      newPublisher.on('streamCreated', (event) => {
        DEBUG_LOG('[ot] publisher mounted, setting tracks');
        setLocalVideoTrack(newPublisher?.getVideoSource?.().track || null);
        setLocalAudioTrack(newPublisher?.getAudioSource?.() || null);
        setPermissionChanged('allowed');
        setPublisherMounted(true);
        event.target.element?.getElementsByTagName('video')?.[0]?.play();
      });
    };

    initPub();

    return () => {
      cancel = true;
      clearTimeout(retryTimeout);
      setPublisherMounted(false);
      setPublisher(null);
      if (newPublisher) {
        DEBUG_LOG('unmounting publisher');
        newPublisher.destroy();
      }
    };
  }, [setPublisher, user, enableAV, selfVideoMounted, shouldMountPublisher, partyKey]);

  useEffect(() => {
    let cancel = false;
    (async () => {
      if (
        publisher &&
        videoDeviceId &&
        publisher.getVideoSource?.()?.deviceId &&
        publisher.getVideoSource?.()?.deviceId !== videoDeviceId
      ) {
        try {
          if (await OTVideoController.isValidDevice(videoDeviceId, 'videoInput')) {
            if (cancel) return;
            DEBUG_LOG('[ot] Setting video source to: ', videoDeviceId);

            await publisher.setVideoSource(videoDeviceId);
            DEBUG_LOG('[ot] set video device successfully: ', videoDeviceId);

            localStorage.setItem(MOST_RECENT_VIDEO_KEY, videoDeviceId);
            setLocalVideoTrack(publisher.getVideoSource?.()?.track);
          }
        } catch (err) {
          console.error('[ot] error setting video device for id: ', videoDeviceId);
        }
      }
    })();

    return () => {
      cancel = true;
    };
  }, [publisher, videoDeviceId, forceUpdate, setLocalVideoTrack]);

  useEffect(() => {
    let cancel = false;
    DEBUG_LOG(
      '[ot] audioDeviceId: ',
      audioDeviceId,
      'deviceId: ',
      publisher?.getAudioSource?.()?.getSettings?.()?.deviceId,
      publisher?.stream,
      publisher?.stream?.hasAudio
    );

    if (
      publisher &&
      audioDeviceId &&
      publisher.getAudioSource?.()?.getSettings?.()?.deviceId &&
      publisher.getAudioSource?.()?.getSettings?.()?.deviceId !== audioDeviceId
    ) {
      DEBUG_LOG('[ot] Setting audio source to: ', audioDeviceId);
      publisher
        .setAudioSource(audioDeviceId)
        .then((val) => {
          if (!cancel) {
            DEBUG_LOG('[ot] set audio device successfully ', audioDeviceId);
            localStorage.setItem(MOST_RECENT_AUDIO_KEY, audioDeviceId);
            setLocalAudioTrack(publisher.getAudioSource?.());
          }
        })
        .catch((err) => {
          if (!cancel) {
            console.error('[ot] set audio device error ', err);
          }
        });
    }
    return () => {
      cancel = true;
    };
  }, [publisher, audioDeviceId, setLocalAudioTrack]);

  useEffect(() => {
    if (publisher && publisher.stream && publisherMounted) {
      if (publisher.stream?.hasVideo !== localVideoEnabled) {
        publisher.publishVideo(localVideoEnabled);
        setLocalVideoTrack(publisher.getVideoSource?.()?.track);
      }

      if (publisher.stream?.hasAudio !== !isAudioInputBlocked) {
        publisher.publishAudio(!isAudioInputBlocked);
        setLocalAudioTrack(publisher.getAudioSource?.());
      }
    }
  }, [publisher, publisher?.stream, localVideoEnabled, isAudioInputBlocked, publisherMounted]);

  useEffect(() => {
    if (
      publisher &&
      publisher.stream &&
      publisherMounted &&
      localVideoTrack !== publisher?.getVideoSource?.()?.track
    ) {
      setLocalVideoTrack(publisher.getVideoSource?.()?.track);
    }
  }, [publisher, publisher?.stream, localVideoTrack, isAudioInputBlocked, publisherMounted]);

  useEffect(() => {
    const ping = () => {
      if (publisher) {
        if (localVideoTrack !== publisher?.getVideoSource?.()?.track) {
          setLocalVideoTrack(publisher?.getVideoSource?.()?.track || null);
        }
        if (localAudioTrack !== publisher?.getAudioSource?.()) {
          setLocalAudioTrack(publisher?.getAudioSource?.() || null);
        }
      }
    };

    const intererval = setInterval(ping, 1000);
    return () => {
      clearInterval(intererval);
    };
  }, [setLocalVideoTrack, setLocalAudioTrack, publisher, localVideoTrack, localAudioTrack]);

  useEffect(() => {
    if (!publisher || !session || !sessionConnected) {
      return;
    }

    if (publisher.session === session) {
      return;
    }

    let cancel = false;
    let retryTimeout: NodeJS.Timeout | null = null;

    DEBUG_LOG('[ot] about to publish publisher: ', publisher?.id, session?.sessionId);

    const publishCallback = (err?: OT.OTError) => {
      if (cancel) return;
      DEBUG_LOG('[ot] published ', publisher.id);
      if (err) {
        if (
          err.message.startsWith(
            'Session.publish :: SetRemoteDescription: Publisher PeerConnection with connection symphony.mantis013-pdx.tokbox.com failed: Error while setting RemoteDescription InvalidAccessError: Offer and answer have different number of m-lines'
          )
        ) {
          DEBUG_LOG('[ot] server/user mismatch, retrying');
          retryTimeout = setTimeout(() => {
            if (cancel) return;
            session.publish(publisher, publishCallback);
          }, 2000);
        }
        console.error('[ot] published error: ', err);
      }

      setLocalVideoTrack(publisher?.getVideoSource?.()?.track || null);
      setLocalAudioTrack(publisher?.getAudioSource?.() || null);
    };

    session.publish(publisher, publishCallback);

    return () => {
      clearTimeout(retryTimeout as NodeJS.Timeout);
      cancel = true;

      // if we don't destroy, next time we publish we get local black screen
      if (publisher) {
        publisher.destroy();
      }
    };
  }, [publisher, session, sessionConnected]);

  const setLocalVideoEnabled = useCallback(
    (enabled: boolean) => {
      _setLocalVideoEnabled(enabled);
      if (publisher) {
        publisher.publishVideo(enabled);
        setLocalVideoTrack(publisher.getVideoSource?.().track);
      }
    },
    [_setLocalVideoEnabled, publisher]
  );

  const setAudioInputBlocked = useCallback(
    (enabled: boolean) => {
      dispatch({
        type: ActionType.SetIsAudioInputBlocked,
        isAudioInputBlocked: enabled,
      });
      // _setAudioInputBlocked(enabled);
      if (publisher) {
        publisher.publishAudio(enabled);
        setLocalAudioTrack(publisher?.getAudioSource?.() || null);
      }
      if (!hasBeenUnmuted && enabled) {
        hasBeenUnmuted = true;
        LOG_EVENT('umute_mic_first_time', { party_key: partyKey });
      }
    },
    [dispatch, publisher, partyKey]
  );

  const getSelfThumbnailImageData = useCallback(() => {
    if (publisher) {
      return publisher.getImgData();
    }
    return null;
  }, [publisher]);

  const getDebugInfo = useCallback(
    (party: IPartyForUser, currentParticipant: IParticipantForUser) => {
      const participantsInSameArea = Object.values(party.stage.participants).filter(
        (p) => p.currentArea === currentParticipant.currentArea && p.uid !== currentParticipant.uid
      );
      const spotlightedParticipantId = party.stage.spotlight?.participant?.uid;
      const spotlightedParticipant = Object.values(party.stage.participants).find(
        (p) => !!spotlightedParticipantId && p.uid === spotlightedParticipantId
      );
      return {
        localVideoTrack: serialize(localVideoTrack || {}),
        localAudioTrack: serialize(localAudioTrack || {}),
        enableAV,
        spotlightedParticipant: serialize({
          ...spotlightedParticipant,
          stream: !!spotlightedParticipantId ? streams[spotlightedParticipantId] || {} : {},
        }),
        stream: serialize(party?.stage?.stream || {}),
        participantsInSameArea: serialize(
          participantsInSameArea.reduce(
            (acc, curr) => ({
              ...acc,
              [curr.uid]: {
                ...curr,
                stream: streams[curr.uid] || {},
              },
            }),
            {}
          )
        ),
      };
    },
    [localVideoTrack, localAudioTrack, enableAV, streams]
  );

  return (
    <VideoControlContext.Provider
      value={{
        isAudioForceMuted,
        isVideoForceMuted,
        isAudioInputBlocked,
        setAudioInputBlocked,
        localVideoEnabled,
        setLocalVideoEnabled,
        localVideoTrack,
        localAudioTrack,
        requestLocalStreamPermissions,
        remoteStreams,
        audioDevices,
        videoDevices,
        videoController: vc,
        hasAVBeenSet: !!localStorage.getItem(HAS_AV_BEEN_SET),
        setHasAVBeenSet,
        setLocalVideo,
        setLocalAudio,
        setParticipantsInSameArea,
        setSession: vc?.setSession,
        connectToSession,
        setSelfVideo,
        setNeedsAVForArea,
        cleanupMediaDeviceAccess,
        session,
        streams,
        selfVideoStream: publisher?.stream || null,
        needsAVForArea,
        setStreams,
        getSelfThumbnailImageData,
        getDebugInfo,
        avPermission,
        sessionConnected,
      }}>
      {children}
    </VideoControlContext.Provider>
  );
}

export const VideoControlProvider = connect(AppMapProps)(_VideoControlProvider);

interface ContextProps {
  isAudioForceMuted: boolean;
  isVideoForceMuted: boolean;
  isAudioInputBlocked: boolean;
  setAudioInputBlocked: (enabled: boolean) => void;
  localVideoEnabled: boolean;
  setLocalVideoEnabled: (enabled: boolean) => void;
  localVideoTrack: MediaStreamTrack | null;
  localAudioTrack: MediaStreamTrack | null;
  remoteStreams: Partial<{ [uid in TUserId]: MediaStream }>;
  videoController: VideoChatController | null;
  hasAVBeenSet: boolean;
  setHasAVBeenSet: (hasBeenSet: boolean) => void;
  requestLocalStreamPermissions: () => void;
  audioDevices: OT.Device[];
  videoDevices: OT.Device[];
  setLocalVideo: (deviceId: string) => void;
  setLocalAudio: (deviceId: string) => void;
  setParticipantsInSameArea: (currentParticipantArea: TAreaKey, stage: IPartyStageForUser) => void;
  setSession: ((sessionId?: string) => void) | undefined;
  sessionConnected: boolean;
  connectToSession: ((sessionId?: string, sessionToken?: string) => void) | undefined;
  cleanupMediaDeviceAccess: () => void;
  session: OT.Session | null;
  streams: Partial<{ [id in TUserId]: OT.Stream }>;
  setSelfVideo: (ref: HTMLDivElement | null) => void;
  setNeedsAVForArea: (needsAV: boolean) => void;
  selfVideoStream: OT.Stream | null;
  needsAVForArea: boolean;
  avPermission: TAVPermission;
  setStreams: (
    value: React.SetStateAction<
      Partial<{
        TUserId: OT.Stream;
      }>
    >
  ) => void;
  getSelfThumbnailImageData: () => string | null;
  getDebugInfo: (party: IPartyForUser, currentParticipant: IParticipantForUser) => Object;
}

const VideoControlContext = React.createContext<ContextProps>({} as ContextProps);

export const useVideoController = () => useContext(VideoControlContext);

export const useSubscription = ({
  shouldSubscribe,
  participant,
  subscribeToAudio,
  subscribeToVideo,
  options = {},
}: {
  shouldSubscribe: boolean;
  participant: IParticipantForUser;
  subscribeToAudio: boolean;
  subscribeToVideo: boolean;
  options?: {
    enableAudioLevel?: boolean;
    isScreenshare?: boolean;
  };
}) => {
  const { uid, photoURL } = participant;
  const { enableAudioLevel = false, isScreenshare = false } = options;
  const { session, streams, setStreams } = useVideoController();
  const [subscriber, setSubscriber] = useState<OT.Subscriber | null>(null);
  const [videoEnabled, setVideoEnabled] = useState<boolean>(false);
  const [subInitialized, setSubInitialized] = useState<boolean>(false);
  const [videoContainerRef, setVideoContainerRef] = useState<HTMLElement | null>(null);
  const [audioLevel, setAudioLevel] = useState<number>(0);
  const retryAttempts = useRef<number>(0);
  const retryHandle = useRef<any>(null);
  const hasPlayed = useRef<boolean>(false);
  const key = options?.isScreenshare ? `screenshare-${uid}` : uid;
  const stream = streams[key as TUserId];

  useEffect(() => {
    const vidEl = videoContainerRef?.getElementsByTagName('video')[0];
    if (vidEl) {
      vidEl.onplay = () => {
        DEBUG_LOG('VIDEO PLAYED');
        hasPlayed.current = true;
        vidEl.onplay = null;
      };
    }
  }, [videoContainerRef]);

  useEffect(() => {
    let cancel = false;

    if (
      !stream ||
      !session ||
      !session.connection ||
      !videoContainerRef ||
      cancel ||
      !shouldSubscribe
    ) {
      return;
    }

    // if (session.getSubscribersForStream(stream).length) {
    //   const subby = session.getSubscribersForStream(stream)[0];
    //   setSubscriber(subby);
    //   setSubInitialized(true);
    //   return;
    // }

    const handleRetrySubscriber = () => {
      if (cancel) return;

      retryHandle.current = setTimeout(() => {
        retryAttempts.current++;
        createSubscriber();
      }, 1000);
    };

    let sub: OT.Subscriber;
    const createSubscriber = () => {
      if (cancel) return;

      DEBUG_LOG('[ot] subscribing to ', stream.name);
      sub = session.subscribe(
        stream,
        videoContainerRef,
        {
          insertMode: 'append',
          width: '100%',
          height: '100%',
          subscribeToVideo: false,
          subscribeToAudio: false,
          fitMode: 'cover',
          style: {
            audioBlockedDisplayMode: 'off',
            audioLevelDisplayMode: 'off',
            backgroundImageURI: photoURL,
            buttonDisplayMode: 'off',
            nameDisplayMode: 'off',
            videoDisabledDisplayMode: 'off',
          },
          testNetwork: true,
        },
        (err) => {
          if (err && retryAttempts.current < MAX_VIDEO_CONNECTION_RETRY_ATTEMPTS && !cancel) {
            console.error('ERROR subscribing: ', err);
            handleRetrySubscriber();
            return;
          }
          DEBUG_LOG('[ot] subscribed successfully ', stream.name);
          setSubInitialized(true);
        }
      );

      sub.on('videoEnabled', (e) => {
        DEBUG_LOG('[ot] videoEnabled', e);
        if (e.target.stream?.hasVideo && !cancel) {
          setVideoEnabled(true);
          const el = sub.element?.getElementsByTagName('video')?.[0];

          // In some browsers it won't autoplay the video unless the window has been interacted with
          // So, save for for after the first click
          el?.play()
            .then()
            .catch((err) => {
              playOnInteract.set(el, () => el.play());
            });

          setStreams((streams) => ({
            ...streams,
            [e.target.stream?.name as TUserId]: e.target.stream,
          }));
        }
      });

      sub.on('videoDisabled', (e) => {
        DEBUG_LOG('[ot] videoDisabled', e);
        if (cancel) return;
        setVideoEnabled(false);
      });

      sub.on('destroyed', (e) => {
        DEBUG_LOG('[ot] sub destroyed', e);
        cancel = true;
        setSubscriber(null);
        setSubInitialized(false);
      });

      if (enableAudioLevel) {
        let movingAvg = 0;
        sub.on('audioLevelUpdated', (e) => {
          if (cancel) return;
          if (movingAvg === 0 || movingAvg <= e.audioLevel) {
            movingAvg = e.audioLevel;
          } else {
            movingAvg = 0.7 * movingAvg + 0.3 * e.audioLevel;
          }
          // 1.5 scaling to map the -30 - 0 dBm range to [0,1]
          var logLevel = Math.log(movingAvg) / Math.LN10 / 2 + 1;
          logLevel = Math.min(Math.max(logLevel, 0), 1);
          setAudioLevel(logLevel);
        });
      }

      setSubscriber(sub);
    };
    createSubscriber();

    return () => {
      // @ts-ignore undocumented fuction I suppose
      const isSubscribing = sub.isSubscribing();

      if (session && isSubscribing) {
        DEBUG_LOG('unsubscribing due to unmounting');
        session.unsubscribe(sub);
      }

      setSubscriber(null);
      setSubInitialized(false);
      cancel = true;
    };
  }, [videoContainerRef, session, stream, enableAudioLevel, photoURL, shouldSubscribe, setStreams]);

  useEffect(() => {
    if (subscriber?.stream && subInitialized) {
      subscriber.subscribeToAudio(subscribeToAudio);
      subscriber.subscribeToVideo(subscribeToVideo);
    }
  }, [
    subscribeToAudio,
    subscribeToVideo,
    subscriber,
    subscriber?.stream,
    subInitialized,
    videoEnabled,
  ]);

  return {
    setVideoContainerRef,
    videoEnabled,
    audioLevel,
  };
};
